import { useEffect, useState } from "react"
import { Link, useNavigate, useSearchParams } from "react-router-dom"
import { AlertCircle, ArrowLeft, CheckCircle2, Loader2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { BrandLogo } from "@/components/ui/brand-logo"
import { apiClient } from "@/api/client"
import { useLanguage } from "@/hooks/useLanguage"

type VerificationStatus = "loading" | "success" | "error"

const VerifyEmail = () => {
  const { t } = useLanguage()
  const text = t.verifyEmail
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [status, setStatus] = useState<VerificationStatus>("loading")

  useEffect(() => {
    const userId = searchParams.get("userId")
    const token = searchParams.get("token")

    if (!userId || !token) {
      setStatus("error")
      return
    }

    apiClient
      .verifyEmail(userId, token)
      .then(() => setStatus("success"))
      .catch(() => setStatus("error"))
  }, [searchParams])

  const isLoading = status === "loading"
  const isSuccess = status === "success"
  const Icon = isLoading ? Loader2 : isSuccess ? CheckCircle2 : AlertCircle

  return (
    <main className="flex min-h-screen items-center justify-center bg-background px-6 py-12 text-foreground">
      <div className="w-full max-w-lg">
        <div className="mb-8 flex items-center justify-between">
          <Link
            to="/"
            className="inline-flex items-center text-xs font-black uppercase tracking-label text-muted-foreground transition-colors hover:text-foreground"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            {text.backHome}
          </Link>
          <BrandLogo className="h-12" />
        </div>

        <div className="border border-border bg-card p-8 shadow-xl sm:p-10">
          <div
            className={`mb-8 flex h-14 w-14 items-center justify-center rounded-sm ${
              isSuccess
                ? "bg-primary text-primary-foreground"
                : isLoading
                  ? "bg-muted text-muted-foreground"
                  : "bg-destructive/10 text-destructive"
            }`}
          >
            <Icon className={`h-7 w-7 ${isLoading ? "animate-spin" : ""}`} />
          </div>

          <div className="space-y-4">
            <h1 className="font-display text-4xl uppercase leading-none tracking-wide">
              {isLoading ? text.loadingTitle : isSuccess ? text.successTitle : text.errorTitle}
            </h1>
            <p className="text-sm leading-6 text-muted-foreground">
              {isLoading
                ? text.loadingDescription
                : isSuccess
                  ? text.successDescription
                  : text.errorDescription}
            </p>
          </div>

          <div className="mt-8">
            <Button
              type="button"
              disabled={isLoading}
              onClick={() => navigate("/login")}
              className="h-12 rounded-sm px-6 font-bold"
            >
              {text.goToLogin}
            </Button>
          </div>
        </div>
      </div>
    </main>
  )
}

export default VerifyEmail
